import { FC } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  formattedCurrency,
  VisaDirectPushTransactionResponse,
  Card,
  withStyles,
  capitalizeFirstLetter,
} from 'vopay';
import { TransactionCard, Title, DetailsRows, TransactionStatus } from 'UI';
import styles from '../Transactions.module.scss';

const cn = withStyles(styles);

type VisaDirectPushTransactionInfoProps = {
  transaction: VisaDirectPushTransactionResponse;
  isParent?: boolean;
};

type DataProps = {
  transactionId: string;
  hasErrorInfo: boolean;
  failureReason: string;
};

const VisaDirectPushTransactionInfo: FC<VisaDirectPushTransactionInfoProps> = ({ transaction, isParent = false }) => {
  const { state } = useLocation<DataProps>();
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.VisaDirectPushTransactionInfo',
  });
  const { t: tGlobal } = useTranslation('translations');

  const transactionDetails = [
    {
      label: t('TransactionDetails.transactionStatus', { defaultValue: 'Transaction Status' }),
      value: (
        <Box jc="fe">
          <TransactionStatus
            status={transaction.TransactionStatus}
            label={capitalizeFirstLetter(
              tGlobal(`Transaction.TransactionHistory.statuses.${transaction.TransactionStatus}`, {
                defaultValue: transaction.TransactionStatus,
              }),
            )}
          />
        </Box>
      ),
      visible: isParent,
    },
    {
      label: t('TransactionDetails.transactionDateTime', { defaultValue: 'Transaction Date Time' }),
      value: transaction.TransactionDateTime,
    },
    {
      label: t('TransactionDetails.lastModified', { defaultValue: 'Last Modified' }),
      value: transaction.LastModified,
    },
    {
      label: t('TransactionDetails.type', { defaultValue: 'Transaction Type' }),
      value: t('visaDirectPush'),
    },
    {
      label: t('TransactionDetails.amount', { defaultValue: 'Amount' }),
      value: `${transaction.Currency} ${formattedCurrency(transaction.Amount)}`,
    },
    {
      label: t('TransactionDetails.holdAmount', { defaultValue: 'Hold Amount' }),
      value: transaction.HoldAmount ? `${transaction.Currency} ${formattedCurrency(transaction.HoldAmount)}` : '-',
    },
    {
      label: t('TransactionDetails.clientReferenceNumber', { defaultValue: 'Client Reference Number' }),
      value: transaction.ClientReferenceNumber || '-',
    },
  ];

  const cardDetails = [
    {
      label: t('CardDetails.name', { defaultValue: 'Name' }),
      value: transaction.FirstName + transaction.LastName,
    },
    {
      label: t('CardDetails.cardNumber', { defaultValue: 'Card Number' }),
      value: transaction.MaskedCardNumber || '-',
    },
  ];

  const errorDetails = [
    {
      label: t('ErrorInfo.failureDateTime', { defaultValue: 'Failure Date Time' }),
      value: transaction.LastModified,
    },
    {
      label: t('ErrorInfo.amount', { defaultValue: 'Amount' }),
      value: `${transaction.Currency} ${formattedCurrency(transaction.Amount)}`,
    },
    {
      label: t('ErrorInfo.failureReason', { defaultValue: 'Failure Reason' }),
      value: state?.failureReason || '-',
    },
  ];

  return (
    <Box flx="col" gapSize="24px">
      {!isParent && (
        <TransactionCard
          receiverName={transaction.FirstName + ' ' + transaction.LastName}
          amount={transaction.Amount}
          currency={transaction.Currency}
          transactionId={transaction.TransactionID}
          status={transaction.TransactionStatus}
          type={'Visa Direct Push'}
          transactionDate={transaction.TransactionDateTime}
          lastModified={transaction.LastModified}
          failureReason={state?.failureReason}
        />
      )}

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('transactionDetailsTitle', { defaultValue: 'Transaction Details' })}
        </Title>
        <DetailsRows className={cn('rows')} details={transactionDetails} />
      </Card>

      <Card className={cn('card')}>
        <Title level="4" className={cn('title')}>
          {t('cardDetailsTitle', { defaultValue: 'Card Details' })}
        </Title>
        <DetailsRows className={cn('rows')} details={cardDetails} />
      </Card>

      {state?.hasErrorInfo && (
        <Card className={cn('card')}>
          <Title level="4" className={cn('title')}>
            {t('errorInfoTitle', { defaultValue: 'Error Info' })}
          </Title>
          <DetailsRows className={cn('rows')} details={errorDetails} />
        </Card>
      )}
    </Box>
  );
};

export default VisaDirectPushTransactionInfo;
